import { useStore } from '@/utils/useStore'
import PlacesStore from '@/store/places'
import EventsStore from '@/store/events'
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import WindowStore from '@/store/window'
import { sentimentCreateAPI, sentimentFollowAPI, sentimentSkipAPI } from '@/api/sentiments'
import { IEvent, IPlace, IUser, Reactive, Result, SentimentTypes, User } from '@/classes'

export default defineStore('sentiments', () => {
  const { getItem, getItems, setItem, deleteItem } = useStore('sentiment')

  const placesStore = PlacesStore()
  const eventStore = EventsStore()
  const windowStore = WindowStore()
  const usersStore = UsersStore()
  const userStore = UserStore()
  const { following, followers, currentUser } = storeToRefs(userStore)

  const route = useRoute()
  const router = useRouter()

  const create = async (
    type: typeof SentimentTypes[number],
    items: {
      eventId?: string
      placeId?: string
      groupId?: string
    },
    options?: {
      action?: 'create' | 'delete'
    }
) => {    
    const response: Result<{
      place: IPlace | null
      event: IEvent | null
      success: boolean
    }> = await sentimentCreateAPI(type, { ...items, ...(options ?? {}) })

    if (!response.ok || !response.data) {
      return { ok: false }
    }

    if (response.data.place) {
      placesStore.setItem(response.data.place, { priority: 5 })
    }

    if (response.data.event) {
      eventStore.setItem(response.data.event, { priority: 5 })
    }

    return {
      ok: true,
      data: response.data
    }
  }

  const follow = async (
    user: User
  ) => {
    const action = following.value.includes(user._id) ? 'unfollow' : 'follow'
  
    useNuxtApp().$amplitude.logEvent(`User_${capitalize(action)}`, {
      source: route.fullPath
    })
      
    if (action === 'follow' && followers.value.includes(user._id)) {
      useNuxtApp().$amplitude.logEvent(`User_match`, {
        source: route.fullPath
      })
      
      router.replace({ query: { ...route.query, userMatch: user.username } })
    } else if (action === 'follow') {
      windowStore.playSound('follow_new')
    }
  
    const response = await sentimentFollowAPI(user._id, action)

    if (response.ok) {
      if (response.data.user) usersStore.setItem(response.data.user, { priority: 5 })
  
      if (response.data.following) following.value = response.data.following
      if (response.data.followers) followers.value = response.data.followers
    }

    return true
  }

  const skipped: Reactive<{
    length: number
    eventIds: string[]
    placeIds: string[]
    groupIds: string[]
    userIds: string[]
    postIds: string[]
  }> = reactive({
    length: 0,
    eventIds: [],
    placeIds: [],
    groupIds: [],
    userIds: [],
    postIds: []
  })

  const skip = async (ids: {
    eventId?: string
    placeId?: string
    groupId?: string
    userId?: string
    postId?: string
  }) => {
    if (ids.eventId) skipped.eventIds.push(ids.eventId)
    if (ids.placeId) skipped.placeIds.push(ids.placeId)
    if (ids.groupId) skipped.groupIds.push(ids.groupId)
    if (ids.userId) skipped.userIds.push(ids.userId)
    if (ids.postId) skipped.postIds.push(ids.postId)

    skipped.length = skipped.eventIds.length + skipped.placeIds.length + skipped.groupIds.length + skipped.userIds.length + skipped.postIds.length
  }

  watchDebounced(skipped, async () => {
    if (skipped.length === 0) return

    const copy = {
      eventIds: [...skipped.eventIds],
      placeIds: [...skipped.placeIds],
      groupIds: [...skipped.groupIds],
      userIds: [...skipped.userIds],
      postIds: [...skipped.postIds]
    }

    const response = await sentimentSkipAPI(skipped)

    if (response.ok && response.data.success) {
      skipped.eventIds = skipped.eventIds.filter(id => !copy.eventIds.includes(id))
      skipped.placeIds = skipped.placeIds.filter(id => !copy.placeIds.includes(id))
      skipped.groupIds = skipped.groupIds.filter(id => !copy.groupIds.includes(id))
      skipped.userIds = skipped.userIds.filter(id => !copy.userIds.includes(id))
      skipped.postIds = skipped.postIds.filter(id => !copy.postIds.includes(id))

      skipped.length = skipped.eventIds.length + skipped.placeIds.length + skipped.groupIds.length + skipped.userIds.length + skipped.postIds.length
    }
  }, { debounce: 3000, maxWait: 5000 })

  return {
    create, follow, skip,
    setItem, deleteItem,
    getItems, getItem
  }
})